import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Container, Link, Snackbar, TextField, Typography } from '@mui/material';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import SendIcon from '@mui/icons-material/Send';
import api from '../api';
import { Link as RouterLink } from 'react-router-dom';

const HighSchoolContact: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/contact', { ...form, type: 'high-school' });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Your message could not be sent. Please try again.');
      }
      setFeedback({ message: 'Your message has been sent. Our school support team will get back to you.', severity: 'success' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (error: any) {
      setFeedback({
        message: error?.response?.data?.message || error?.message || 'Your message could not be sent. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <ContactSupportOutlinedIcon color="primary" sx={{ fontSize: 44, mb: 1 }} />
        <Typography variant="h4" component="h1" fontWeight={800} gutterBottom>
          Contact A3 High School
        </Typography>
        <Typography color="text.secondary">
          Questions about lessons, mock tests, school work, or your account? Send our student support team a message.
        </Typography>
      </Box>
      <Alert
        severity="info"
        sx={{ mb: 3 }}
        action={
          <Button component={RouterLink} to="/helpdesk" color="inherit" size="small">
            Open Helpdesk
          </Button>
        }
      >
        This page is for education support. For website or app errors, sign-in problems, or other technical issues, use the technical Helpdesk.
      </Alert>
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2.5 }}>
            <TextField
              label="Your name"
              autoComplete="name"
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            <TextField
              label="Email address"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
            <TextField
              label="Subject"
              required
              value={form.subject}
              onChange={(event) => setForm({ ...form, subject: event.target.value })}
            />
            <TextField
              label="How can we help?"
              required
              multiline
              minRows={5}
              value={form.message}
              onChange={(event) => setForm({ ...form, message: event.target.value })}
            />
            <Button type="submit" variant="contained" startIcon={<SendIcon />} disabled={loading}>
              {loading ? 'Sending...' : 'Send message'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help with a website or app issue instead?{' '}
          <Link component={RouterLink} to="/helpdesk" underline="hover" fontWeight={700}>
            Visit the technical Helpdesk
          </Link>
          .
        </Typography>
      </Box>
      <Snackbar open={Boolean(feedback)} autoHideDuration={6000} onClose={() => setFeedback(null)}>
        {feedback ? <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert> : undefined}
      </Snackbar>
    </Container>
  );
};

export default HighSchoolContact;
